import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { translationPropType } from '@eb/i18n';
import { lazyGettext } from '@eb/lazy-gettext';

import { getAdditionalProps, datePropType } from '@eb/eds-utils';
import { DEFAULT_INPUT_DISPLAY_FORMAT } from '@eb/eds-utils';

import RangePickerController from './_internal/RangePickerController';
import DateRangeInput from './_internal/DateRangeInput';
import * as constants from './constants';

import './datePicker.scss';

export default class DateRangePicker extends PureComponent {
    static propTypes = {
        /**
         * Initial date
         */
        defaultValue: PropTypes.shape(constants.RANGE_VALUE),
        /**
         * Current selected date
         */
        value: PropTypes.shape(constants.RANGE_VALUE),
        /**
         * Calendar color scheme
         */
        color: PropTypes.oneOf(constants.COLOR_PROP),
        /**
         * Minimum date that can be chosen
         */
        minDate: datePropType,
        /**
         * Maximum date that can be chosen
         */
        maxDate: datePropType,
        /**
         * Set error state
         */
        hasError: PropTypes.bool,
        /**
         * {function} for handling date selection
         */
        onChange: PropTypes.func,
        /**
         * {function} for handling blur event
         */
        onBlur: PropTypes.func,
        /**
         * {function} onFocus
         */
        onFocus: PropTypes.func,
        /**
         * Moment formatting date string to dictate display format of selected
         * dates ie. "MMM D" or any format found at: https://momentjs.com/docs/#/displaying/format/
         */
        inputDisplayFormat: PropTypes.string,
        /**
         * Placeholder text for the start date on the input
         */
        inputStartDatePlaceholder: translationPropType,
        /**
         * Placeholder text for the end date on the input
         */
        inputEndDatePlaceholder: translationPropType,
        /**
         * The node which exists before the start and end
         * reperesentations.
         */
        inputDelimeter: PropTypes.node,
        /**
         * The calendar should open focused on the passed in option.
         * Available options: startDate, endDate
         */
        initialFocusedInput: PropTypes.oneOf(constants.INITIAL_FOCUS),
    };

    static defaultProps = {
        inputStartDatePlaceholder: lazyGettext('Start date'),
        inputEndDatePlaceholder: lazyGettext('End date'),
        inputDisplayFormat: DEFAULT_INPUT_DISPLAY_FORMAT,
        color: constants.BLUE_COLOR,
    };

    constructor(props) {
        super(props);

        const { initialFocusedInput, defaultValue, value } = props;

        let startDate = null;
        let endDate = null;

        if (defaultValue) {
            startDate = defaultValue.startDate;
            endDate = defaultValue.endDate;
        }

        if (value) {
            startDate = value.startDate;
            endDate = value.endDate;
        }

        this.state = {
            startDate,
            endDate,
            focusedInput: initialFocusedInput,
        };
    }

    componentWillReceiveProps({ value = {} }) {
        this.setState(({ startDate, endDate }) => {
            const { startDate: newStartDate, endDate: newEndDate } = value;
            let startDateToReturn = startDate;
            let endDateToReturn = endDate;

            if (newStartDate && newStartDate !== startDate) {
                startDateToReturn = newStartDate;
            }

            if (newEndDate && newEndDate !== endDate) {
                endDateToReturn = newEndDate;
            }

            return { startDate: startDateToReturn, endDate: endDateToReturn };
        });
    }

    _handleOnClose = ({ startDate, endDate }) => {
        const { onBlur } = this.props;

        if (onBlur) {
            onBlur({ startDate, endDate });
        }
    };

    _handleFocus = (focusedInput) => {
        const { onFocus } = this.props;
        const { startDate, endDate } = this.state;

        this.setState({ focusedInput }, () => {
            if (onFocus) {
                onFocus({ startDate, endDate, focusedInput });
            }
        });
    };

    _handleDatesChange = ({ startDate, endDate }) => {
        const { onChange } = this.props;

        this.setState({ startDate, endDate }, () => {
            if (onChange) {
                onChange({ startDate, endDate });
            }
        });
    };

    _handleClickOutside = () => {
        const { onBlur } = this.props;
        const { startDate, endDate } = this.state;

        this.setState({ focusedInput: null }, () => {
            if (onBlur) {
                onBlur({ startDate, endDate });
            }
        });
    };

    render() {
        const {
            color,
            inputEndDatePlaceholder,
            inputStartDatePlaceholder,
            inputDisplayFormat,
            inputDelimeter,
            minDate,
            maxDate,
        } = this.props;
        const { startDate, endDate, focusedInput } = this.state;

        const classes = classNames(
            'eds-date-picker',
            'eds-date-picker--range',
            `eds-date-picker--${color}`,
        );

        const extraAttrs = getAdditionalProps(this);

        return (
            <div className={classes}>
                <DateRangeInput
                    focusedInput={focusedInput}
                    startDate={startDate}
                    endDate={endDate}
                    onFocus={this._handleFocus}
                    startPlaceHolder={inputStartDatePlaceholder}
                    endPlaceHolder={inputEndDatePlaceholder}
                    inputDisplayFormat={inputDisplayFormat}
                    inputDelimeter={inputDelimeter}
                />
                <RangePickerController
                    {...extraAttrs}
                    onFocusChange={this._handleFocus}
                    onDatesChange={this._handleDatesChange}
                    onClose={this._handleOnClose}
                    onClickOutside={this._handleClickOutside}
                    focusedInput={focusedInput}
                    startDate={startDate}
                    endDate={endDate}
                    minDate={minDate}
                    maxDate={maxDate}
                />
            </div>
        );
    }
}
